import {
  type UpdateUser,
  type Word,
  type UpdateUserResponse,
  type LogoutResponse,
  type GoogleAuthResponse,
  type GenerateLicenseResponse,
  type GetUserSubscriptionResponse,
  type UpgradeSubscriptionResponse,
  type DowngradeSubscriptionResponse,
  type CancelSubscriptionResponse,
  type DashboardStatsResponse,
  type GetUserWordsResponse,
  type ExpireTokenResponse,
  type StartSessionData,
  SubscriptionPlan,
  UserStats,
  UserInteraction,
  MarkLearnedData,
  UserAPI,
  ProfileUser,
  GetTodayWordsData,
  GetTodayWordsDataSchema,
} from '@lingugeek/schemas';
import { buildAudioUrl } from '@lingugeek/shared';
import { getApiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

export class ApiService {
  private client = getApiClient();

  async getUser(): Promise<ProfileUser> {
    return this.client.get(API_ENDPOINTS.USER_ME, UserAPI.profile.response);
  }

  async updateUser(data: UpdateUser): Promise<UpdateUserResponse> {
    return this.client.put(API_ENDPOINTS.USER_UPDATE, data);
  }

  async logout(): Promise<LogoutResponse> {
    return this.client.post(API_ENDPOINTS.AUTH_LOGOUT, undefined);
  }

  async authenticateWithGoogle(
    accessToken: string
  ): Promise<GoogleAuthResponse> {
    return this.client.post(API_ENDPOINTS.AUTH_GOOGLE, { token: accessToken });
  }

  async generateLicense(): Promise<GenerateLicenseResponse> {
    return this.client.post(API_ENDPOINTS.USER_LICENSE, undefined);
  }

  async getSubscription(): Promise<GetUserSubscriptionResponse> {
    return this.client.get(API_ENDPOINTS.SUBSCRIPTION_ME);
  }

  async upgradeSubscription(
    plan: SubscriptionPlan
  ): Promise<UpgradeSubscriptionResponse> {
    return this.client.post(API_ENDPOINTS.SUBSCRIPTION_UPGRADE, { plan });
  }

  async downgradeSubscription(
    plan: SubscriptionPlan
  ): Promise<DowngradeSubscriptionResponse> {
    return this.client.post(API_ENDPOINTS.SUBSCRIPTION_DOWNGRADE, { plan });
  }

  async cancelSubscription(): Promise<CancelSubscriptionResponse> {
    return this.client.post(API_ENDPOINTS.SUBSCRIPTION_CANCEL, undefined);
  }

  async getStats(): Promise<UserStats> {
    return this.client.get(API_ENDPOINTS.STATS);
  }

  async getProgress(): Promise<UserStats> {
    return this.client.get(API_ENDPOINTS.STATS_PROGRESS);
  }

  async getInteractions(
    limit = 5,
    options: { journeyOnly?: boolean } = {}
  ): Promise<UserInteraction[]> {
    const journeyOnly = options.journeyOnly ? 'true' : 'false';
    const endpoint = `${API_ENDPOINTS.STATS_INTERACTIONS}?limit=${limit}&journeyOnly=${journeyOnly}`;
    return this.client.get(endpoint);
  }

  async getDashboardData(
    interactionsLimit = 5
  ): Promise<DashboardStatsResponse> {
    const endpoint = `${API_ENDPOINTS.STATS_DASHBOARD}?interactionsLimit=${interactionsLimit}`;
    return this.client.get(endpoint);
  }

  // Words services
  async getWords(query = ''): Promise<GetUserWordsResponse> {
    const endpoint = `${API_ENDPOINTS.WORDS}${query}`;
    return this.client.get(endpoint);
  }

  // Learning services (for webview)
  async getTodaysWords(token: string): Promise<GetTodayWordsData> {
    const endpoint = `${API_ENDPOINTS.LEARN_TODAY}?token=${encodeURIComponent(token)}`;
    return await this.client.get<GetTodayWordsData>(
      endpoint,
      GetTodayWordsDataSchema
    );
  }

  async startSession(token: string): Promise<StartSessionData> {
    return this.client.post(
      `${API_ENDPOINTS.LEARN_START_SESSION}?token=${encodeURIComponent(token)}`,
      undefined
    );
  }

  async markWordAsLearned(
    token: string,
    wordId: string
  ): Promise<MarkLearnedData> {
    return this.client.post(
      `${API_ENDPOINTS.LEARN_MARK_LEARNED}?token=${encodeURIComponent(token)}`,
      { wordId }
    );
  }

  async expireToken(token: string): Promise<ExpireTokenResponse> {
    return this.client.post(
      `${API_ENDPOINTS.LEARN_EXPIRE_TOKEN}?token=${encodeURIComponent(token)}`,
      undefined
    );
  }

  async getWordDetails(wordId: string, token: string): Promise<Word> {
    const endpoint = `${API_ENDPOINTS.LEARN_WORD_DETAIL}/${wordId}?token=${encodeURIComponent(token)}`;
    return this.client.get(endpoint);
  }

  // Collection services
  async getLearnHistory(): Promise<any> {
    // TODO: Add GetLearningHistoryResponse to schemas
    return this.client.get(API_ENDPOINTS.LEARN_HISTORY);
  }

  async getLearnCollection(id: string): Promise<any> {
    // TODO: Add GetCollectionResponse to schemas
    const endpoint = `${API_ENDPOINTS.LEARN_COLLECTION}?id=${id}`;
    return this.client.get(endpoint);
  }

  // Audio services
  getAudioUrl(
    wordId: string,
    type: 'pronunciation' | 'example',
    token?: string
  ): string {
    return buildAudioUrl(API_ENDPOINTS.AUDIO_STREAM, wordId, type, token);
  }

  // Leads service
  async createLead(email: string): Promise<any> {
    // TODO: Add CreateLeadResponse to schemas
    return this.client.post(API_ENDPOINTS.LEADS, { email });
  }
}

// Singleton instance
let apiServiceInstance: ApiService | null = null;

export function createApiService(): ApiService {
  if (apiServiceInstance) {
    return apiServiceInstance;
  }

  apiServiceInstance = new ApiService();
  return apiServiceInstance;
}

export function getApiService(): ApiService {
  if (!apiServiceInstance) {
    throw new Error(
      'API Service not initialized. Call createApiService first.'
    );
  }
  return apiServiceInstance;
}
